import { z } from "zod";

// Shared refinement functions to avoid duplication
const skipExclusivityRefinement = (value: string[]): boolean => {
  const hasSkip = value.includes("Skip this question");
  return !(hasSkip && value.length > 1);
};
const skipExclusivityMessage =
  "You cannot select other options if 'Skip this question' is selected.";

const faithOptions = [
  "Christianity",
  "Islam",
  "Judaism",
  "Hinduism",
  "Buddhism",
  "Spiritual but not Religious",
  "I do not integrate spirituality into my coaching",
  "Other",
] as const;

const faithNoPreferenceRefinement = (
  value: Array<(typeof faithOptions)[number] | "No preference">,
): boolean => {
  const hasNoPreference = value.includes("No preference");
  const hasOtherOptions = value.some((option) =>
    (faithOptions as readonly string[]).includes(option),
  );
  return !(hasNoPreference && hasOtherOptions);
};
const faithNoPreferenceMessage =
  "You cannot select other preferences if 'No preference' is selected.";

// step1
const IdentifySpecificGoalsBase = z.object({
  goals: z
    .array(z.string())
    .min(1, "Please select at least one goal.")
    .max(2, "You cannot select more than 2 goals."),
  otherGoal: z
    .string()
    .max(500, "Your description must be 500 characters or less.")
    .optional(),
});

export const IdentifySpecificGoalsBaseSchema = IdentifySpecificGoalsBase.refine(
  (data) => {
    // If "Other" is selected, otherGoal must have content
    if (data.goals.includes("Other")) {
      return data.otherGoal && data.otherGoal.trim().length > 0;
    }
    return true;
  },
  {
    message: "Please describe your goal when 'Other' is selected.",
    path: ["otherGoal"],
  },
);

export const IdentifySpecificGoalsSchema =
  IdentifySpecificGoalsBaseSchema.transform((input) => ({
    ...input,
    otherGoal: input.otherGoal || undefined,
  }));

export type IdentifySpecificGoalsForm = z.infer<
  typeof IdentifySpecificGoalsSchema
>;

//step2
export const DefineUrgencyAndReadinessSchema = z.object({
  startTime: z
    .string()
    .min(
      1,
      "Please select when you're looking to start working towards your goals.",
    ),
  support: z.string().min(1, "Please select how much support you need."),
});

export type DefineUrgencyAndReadinessForm = z.infer<
  typeof DefineUrgencyAndReadinessSchema
>;

// step 3
const PreferredCoachingStyleAndFormatBase = z.object({
  coachingFormats: z
    .string()
    .min(
      1,
      "Please select the type of coaching experience you are looking for.",
    ),
  coachingApproaches: z
    .array(z.string())
    .min(1, "Please select at least one option."),
});

export const PreferredCoachingStyleAndFormatSchema =
  PreferredCoachingStyleAndFormatBase.refine(
    (data) => {
      // "Not sure yet" is exclusive and doesn't count toward the 2-option limit
      if (data.coachingApproaches.includes("Not sure yet")) {
        return data.coachingApproaches.length === 1;
      }
      // Regular options have a max of 2
      return data.coachingApproaches.length <= 2;
    },
    {
      message: "You cannot select more than 2 options.",
      path: ["coachingApproaches"],
    },
  );

export type PreferredCoachingStyleAndFormatForm = z.infer<
  typeof PreferredCoachingStyleAndFormatSchema
>;

// step 4
export const CoachExperienceLevelSchema = z.object({
  coachingPackages: z
    .array(z.string())
    .min(1, "Please select at least one experience level."),
});

export type CoachExperienceLevelForm = z.infer<
  typeof CoachExperienceLevelSchema
>;

// step 5
export const CoachDemographicsPreferenceSchema = z.object({
  coachGender: z.string().min(1, "Please select your gender preference."),
  coachAges: z
    .array(z.string())
    .min(1, "Please select at least one age preference."),
});

export type CoachDemographicsPreferenceForm = z.infer<
  typeof CoachDemographicsPreferenceSchema
>;

// step 6
export const UserAndCoachPersonalitySchema = z.object({
  userPersonality: z
    .array(z.string())
    .min(1, "Please select at least one option.")
    .max(2, "You cannot select more than 2 options.")
    .refine(skipExclusivityRefinement, skipExclusivityMessage),
  coachPersonality: z
    .array(z.string())
    .min(1, "Please select at least one option.")
    .max(2, "You cannot select more than 2 options.")
    .refine(skipExclusivityRefinement, skipExclusivityMessage),
});

export type UserAndCoachPersonalityForm = z.infer<
  typeof UserAndCoachPersonalitySchema
>;

// step 7
// FormOfConductSchema and FormOfConductForm are defined after CoachDiscoveryBaseSchema
// to derive fields from the base schema shape.

// step 8
const faithIntegrationLiterals = z.union([
  z.literal("Christianity"),
  z.literal("Islam"),
  z.literal("Judaism"),
  z.literal("Hinduism"),
  z.literal("Buddhism"),
  z.literal("Spiritual but not Religious"),
  z.literal("Other"),
  z.literal("I do not integrate spirituality into my coaching"),
  z.literal("No preference"),
]);

const FaithBasedCoachingBaseSchema = z.object({
  faithIntegration: z
    .array(faithIntegrationLiterals)
    .min(1, "Please select at least one preference.")
    .refine(faithNoPreferenceRefinement, faithNoPreferenceMessage),
  otherFaithIntegration: z
    .string()
    .trim()
    .max(500, "Your description must be 500 characters or less.")
    .optional(),
});

export const FaithBasedCoachingSchema = FaithBasedCoachingBaseSchema.refine(
  (data) => {
    // If "Other" is selected, otherFaithIntegration must be provided
    if (data.faithIntegration.includes("Other")) {
      return (
        !!data.otherFaithIntegration &&
        data.otherFaithIntegration.trim().length > 0
      );
    }
    return true;
  },
  {
    message:
      "Please describe your faith in the text area if you selected 'Other'.",
    path: ["otherFaithIntegration"],
  },
);

export type FaithBasedCoachingForm = z.infer<typeof FaithBasedCoachingSchema>;

// step 9
export const PreferredLanguageSchema = z.object({
  preferredLanguages: z
    .array(z.string())
    .min(1, "Please select at least one preferred language.")
    .max(3, "You can select up to 3 languages."),
});

export type PreferredLanguageForm = z.infer<typeof PreferredLanguageSchema>;

// step 10
export const PersonalContext = z.object({
  personalContext: z
    .string()
    .trim()
    .min(1, "Please describe your personal context.")
    .max(500, "Your description must be 500 characters or less."),
});

export type PersonalContextForm = z.infer<typeof PersonalContext>;

// =============================================================================
// BASE SCHEMA (lenient - used for coach-finder, search, and storage)
// All fields have sensible defaults, no .min(1) requirements
// =============================================================================

const CoachPreferencesBaseSchema = z.object({
  // Goals (required even in lenient mode - can't search without a goal)
  goals: z
    .array(z.string())
    .min(1, "Please select at least one goal.")
    .max(2, "You cannot select more than 2 goals."),
  otherGoal: z
    .string()
    .max(500, "Your description must be 500 characters or less.")
    .optional(),

  // Basic filters (required in lenient mode)
  coachingFormats: z
    .string()
    .min(
      1,
      "Please select the type of coaching experience you are looking for.",
    ),
  sessionDeliveryMethods: z
    .string()
    .min(1, "Please select your session format preference."),
  coachGender: z.string().min(1, "Please select your gender preference."),
  coachAges: z
    .array(z.string())
    .min(1, "Please select at least one age preference."),

  // Location preferences
  prefersCoachLocation: z.string().default("No preference"),
  coachLocationCountry: z
    .string()
    .trim()
    .max(100, "Country name must be under 100 characters.")
    .optional()
    .transform((value) => (value === "" ? undefined : value)),
  coachLocationRegion: z
    .string()
    .trim()
    .max(100, "City or region name must be under 100 characters.")
    .optional(),

  // Advanced filters (optional - empty arrays/strings allowed)
  startTime: z.string().default(""),
  support: z.string().default(""),
  coachingApproaches: z
    .array(z.string())
    .max(2, "You can select up to 2 coaching styles.")
    .default([]),
  coachingPackages: z.array(z.string()).default([]),
  userPersonality: z
    .array(z.string())
    .max(2, "You can select up to 2 options.")
    .default([])
    .refine(skipExclusivityRefinement, skipExclusivityMessage),
  coachPersonality: z
    .array(z.string())
    .max(2, "You can select up to 2 options.")
    .default([])
    .refine(skipExclusivityRefinement, skipExclusivityMessage),
  faithIntegration: z
    .array(faithIntegrationLiterals)
    .default([])
    .refine((value) => {
      if (value.length === 0) return true;
      return faithNoPreferenceRefinement(value);
    }, faithNoPreferenceMessage),
  otherFaithIntegration: z
    .string()
    .trim()
    .max(500, "Your description must be 500 characters or less.")
    .optional(),
  preferredLanguages: z
    .array(z.string())
    .max(3, "You can select up to 3 languages.")
    .default([]),
  personalContext: z
    .string()
    .trim()
    .max(500, "Your description must be 500 characters or less.")
    .default(""),

  // UI-only filter
  statusFilters: z.array(z.enum(["founding", "featured"])).default([]),
});

// Cross-field validation refinements applied to full-form schemas
const applySharedRefinements = <T extends z.ZodTypeAny>(schema: T) =>
  schema.superRefine((raw, ctx) => {
    const value = raw as Partial<z.infer<typeof CoachPreferencesBaseSchema>>;

    if (
      Array.isArray(value.goals) &&
      value.goals.includes("Other") &&
      (!("otherGoal" in value) ||
        typeof value.otherGoal !== "string" ||
        value.otherGoal.trim().length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please describe your goal when 'Other' is selected.",
        path: ["otherGoal"],
      });
    }

    if (
      Array.isArray(value.faithIntegration) &&
      value.faithIntegration.includes("Other") &&
      (!("otherFaithIntegration" in value) ||
        typeof value.otherFaithIntegration !== "string" ||
        value.otherFaithIntegration.trim().length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Please describe your faith in the text area if you selected 'Other'.",
        path: ["otherFaithIntegration"],
      });
    }

    if (
      value.prefersCoachLocation === "Yes" &&
      (!value.coachLocationCountry || value.coachLocationCountry.length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please select your country.",
        path: ["coachLocationCountry"],
      });
    }
  });

// =============================================================================
// COACH FINDER SCHEMA (for coach-finder page, search API, and preferences storage)
// Lenient base + cross-field refinements
// =============================================================================

export const CoachFinderSchema = applySharedRefinements(
  CoachPreferencesBaseSchema,
);

// =============================================================================
// DISCOVERY SCHEMA (strict - all fields required for onboarding flow)
// =============================================================================

// Base schema with strict requirements - used for discovery flow validation
export const CoachDiscoveryBaseSchema = CoachPreferencesBaseSchema.extend({
  // Make advanced filters required for discovery
  startTime: z
    .string()
    .min(
      1,
      "Please select when you're looking to start working towards your goals.",
    ),
  support: z.string().min(1, "Please select how much support you need."),
  coachingApproaches: z
    .array(z.string())
    .min(1, "Please select at least one option.")
    .max(2, "You can select up to 2 coaching styles."),
  coachingPackages: z
    .array(z.string())
    .min(1, "Please select at least one experience level."),
  userPersonality: z
    .array(z.string())
    .min(1, "Please select at least one option.")
    .max(2, "You can select up to 2 options.")
    .refine(skipExclusivityRefinement, skipExclusivityMessage),
  coachPersonality: z
    .array(z.string())
    .min(1, "Please select at least one option.")
    .max(2, "You can select up to 2 options.")
    .refine(skipExclusivityRefinement, skipExclusivityMessage),
  faithIntegration: z
    .array(faithIntegrationLiterals)
    .min(1, "Please select at least one preference.")
    .refine(faithNoPreferenceRefinement, faithNoPreferenceMessage),
  preferredLanguages: z
    .array(z.string())
    .min(1, "Please select at least one preferred language.")
    .max(3, "You can select up to 3 languages."),
  personalContext: z
    .string()
    .trim()
    .min(1, "Please describe your personal context.")
    .max(500, "Your description must be 500 characters or less."),
});

export const CoachDiscoverySchema = applySharedRefinements(
  CoachDiscoveryBaseSchema,
);

export type CoachFinderForm = z.infer<typeof CoachFinderSchema>;

export type CoachDiscoveryForm = z.infer<typeof CoachDiscoverySchema>;
export type GoalOptions = CoachDiscoveryForm["goals"][number];
export type CoachingStyleOptions =
  CoachDiscoveryForm["coachingApproaches"][number];
export type CoachExperienceOptions =
  CoachDiscoveryForm["coachingPackages"][number];
export type CoachAgesOptions = CoachDiscoveryForm["coachAges"][number];
export type FaithIntegrationOptions =
  CoachDiscoveryForm["faithIntegration"][number];

export type CoachDiscoveryField = Extract<keyof CoachDiscoveryForm, string>;

// Step schema builder - derives field schemas from CoachDiscoveryBaseSchema
const coachDiscoveryShape = CoachDiscoveryBaseSchema.shape;

// Step 7 schema - derived from base schema with cross-field validation
const FormOfConductBaseSchema = z.object({
  sessionDeliveryMethods: coachDiscoveryShape.sessionDeliveryMethods,
  prefersCoachLocation: coachDiscoveryShape.prefersCoachLocation,
  coachLocationCountry: coachDiscoveryShape.coachLocationCountry,
  coachLocationRegion: coachDiscoveryShape.coachLocationRegion,
});

export const FormOfConductSchema = FormOfConductBaseSchema.superRefine(
  (value, ctx) => {
    if (
      value.prefersCoachLocation === "Yes" &&
      (!value.coachLocationCountry || value.coachLocationCountry.length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please select your country.",
        path: ["coachLocationCountry"],
      });
    }
  },
);

export type FormOfConductForm = z.infer<typeof FormOfConductSchema>;

export type CoachDiscoveryStep<
  Fields extends readonly CoachDiscoveryField[] =
    readonly CoachDiscoveryField[],
> = {
  title: string;
  subTitle?: string;
  fields: Fields;
};
/*[
    "careerBusinessSpecialties",
    "healthFitnessSpecialties",
    "personalDevelopmentSpecialties",
    "relationshipSpecialties",
    "stressWellbeingSpecialties"
    "financialSpecialties"
    "otherSpecialties"
  ] */

export const goalsField = {
  name: "goals",
  options: [
    {
      label: "Advance my career/business",
      value: "Career & Business Coaching",
      description:
        "Business & Leadership Coaching, Mentorship, Executive Coaching",
    },
    {
      label: "Improve my health & fitness",
      value: "Health & Fitness Coaching",
      description: "Health Coaching, Wellness, Energy Work",
    },
    {
      label: "Enhance personal development",
      value: "Personal Development Coaching",
      description: "Life Coaching, Mindset Coaching, Spiritual Guidance",
    },
    {
      label: "Build stronger relationships",
      value: "Relationship, Dating & Family Coaching",
      description: "Relationship Coaching, Communication Skills Coaching",
    },
    {
      label: "Manage stress and well-being",
      value: "Stress, Emotional Resilience & Well-being Coaching",
      description: "Mindfulness Coaching, Therapy, Stress Management",
    },
    {
      label: "Improve financial management",
      value: "Financial & Wealth Coaching",
      description: "Financial Coaching, Wealth Strategy Coaching",
    },
    {
      label: "Enhance sports performance",
      value: "Sports & Performance Coaching",
      description: "Sports Coaching, Performance Training, Self-Defense",
    },
    { value: "Other", label: "Other" },
  ],
};

export const otherGoalField = {
  name: "otherGoal",
  placeholder: "Enter your goals here..",
  maxLength: 500,
};
export const specificGoalsStep: CoachDiscoveryStep = {
  title: "What goals are you looking to achieve with a coach?",
  fields: ["goals", "otherGoal"] as const,
};

export const startTimeField = {
  name: "startTime",
  subTitle: "When would you like to start working with a coach?",
  options: [
    { value: "Immediately", label: "Immediately" },
    { value: "In the next month", label: "In the next month" },
    { value: "In a few months", label: "In a few months" },
    { value: "I'm flexible", label: " I'm flexible" },
  ],
};
export const supportField = {
  name: "support",
  subTitle: "How much support do you need?",
  options: [
    {
      value: "Tactical coaching, consulting, strategy sessions",
      label: "A quick solution for a specific challenge",
      description: "Tactical coaching, consulting, strategy sessions",
    },
    {
      value: "Mentorship, business & career coaching, structured programs",
      label: "Ongoing skill-building & growth",
      description:
        "Mentorship, business & career coaching, structured programs",
    },
    {
      value: "Life coaching, therapy, energy healing, mindset shifts",
      label: "Deep, long-term transformation",
      description: "Life coaching, therapy, energy healing, mindset shifts",
    },
    {
      value: "Not sure yet – I'd like guidance on this",
      label: "Not sure yet – I'd like guidance on this",
    },
  ],
};

export const urgencyAndReadinessStep: CoachDiscoveryStep = {
  title:
    "How soon do you want to start, and what level of support do you need?",
  fields: ["startTime", "support"] as const,
};

//coachingFormats
//coachingApproaches

export const coachingFormatsFields = {
  name: "coachingFormats",
  subTitle: "Your Preferred Format",
  options: [
    { value: "1-on-1", label: "1:1 Coaching only" },
    { value: "both", label: "Open to group coaching or 1:1" },
    { value: "group", label: "Prefer group coaching" },
    { value: "undecided", label: "Not sure yet" },
    { value: "any", label: "No preference" },
  ],
};

/**
 * The coachingFormats value that means "I have no format preference".
 *
 * Unlike every other basic filter, the sentinel here is the option value
 * ("any"), not the "No preference" label the other filters store. The matching
 * engine only skips its hard filter for "any"/"both"/"undecided", so any other
 * value — including the literal string "No preference" — becomes a filter no
 * coach can satisfy.
 */
export const COACHING_FORMAT_NO_PREFERENCE = "any";

const unrestrictedCoachingFormatValues = new Set([
  COACHING_FORMAT_NO_PREFERENCE,
  "both",
  "undecided",
  // Older quick-match rows used the label instead of an option value.
  "No preference",
]);

export const isUnrestrictedCoachingFormat = (
  value: string | null | undefined,
): boolean => !value || unrestrictedCoachingFormatValues.has(value);

const coachingFormatValues = new Set(
  coachingFormatsFields.options.map(({ value }) => value),
);

/**
 * Map a stored coachingFormats value onto one of the options the form offers.
 *
 * Quick match used to persist the "No preference" label instead of the "any"
 * value. That left the format radio group with nothing selected and turned the
 * matching hard filter into one no coach could satisfy, so the coach finder
 * rendered zero matches until the user picked a format by hand. Normalising on
 * read repairs those rows without a migration; anything unrecognised falls back
 * to "no preference".
 */
export const normalizeCoachingFormat = (
  value: string | null | undefined,
): string =>
  value && coachingFormatValues.has(value)
    ? value
    : COACHING_FORMAT_NO_PREFERENCE;

export const coachingApproachesField = {
  name: "coachingApproaches",
  subTitle: "How do you prefer to be guided?",
  options: [
    {
      value: "Exploratory & Self-Reflective",
      label: "Exploratory & Self-Reflective",
      description:
        "Transformational Coaching, Therapy, Healing – Helps uncover answers through deep questioning",
    },
    {
      value: "Goal-oriented & action-driven",
      label: "Goal-oriented & action-driven",
      description:
        "Consulting, Business Coaching, Executive Coaching – structured, clear strategies with defined outcomes",
    },
    {
      value: "Supportive & Encouraging",
      label: "Supportive & Encouraging",
      description:
        "Wellness Coaching, Relationship Coaching – Motivational, compassionate, and accountability-driven",
    },
    { value: "Not sure yet", label: "Not sure yet" },
  ],
};

export const coachingFormatsAndApproachesStep: CoachDiscoveryStep = {
  title: "What type of coaching experience are you looking for?",
  fields: ["coachingFormats", "coachingApproaches"] as const,
};
//coachingPackages
export const coachingPackagesField = {
  name: "coachingPackages",
  subTitle:
    "All Dream Coach Match coaches offer results-based packages, not hourly rates. You're investing in outcomes, not time.",

  options: [
    {
      value: "Rising Coach",
      label: "Rising Coach: Goal-Driven Starter Package",
      description: "Get clear and build momentum with an early-stage coach.",
    },
    {
      value: "Experienced Coach",
      label: "Experienced Coach: Proven Results Program",
      description: "Work with a seasoned professional to achieve your goals.",
    },
    {
      value: "Master Coach",
      label: "Master Coach: Elite Transformation Partnership",
      description:
        "Partner with an industry-leading expert for life-changing results.",
    },
    {
      value: "No preference",
      label: "No preference – I just want the best coach for me",
    },
  ],
};
export const coachPackagesStep: CoachDiscoveryStep = {
  title: "Which coaching package best aligns with your needs?",
  fields: ["coachingPackages"] as const,
};

export const coachGenderField = {
  name: "coachGender",
  subTitle: "Do you have a gender preference?",
  options: [
    { value: "Woman", label: "Woman coach" },
    { value: "Man", label: "Man coach" },
    { value: "Non-Binary", label: "Non-Binary coach" },
    { value: "No preference", label: "No preference" },
  ],
};

export const coachAgesField = {
  name: "coachAges",
  subTitle: "Do you have an age range preference for your coach?",
  options: [
    { value: "20-30", label: "20-30" },
    { value: "30-40", label: "30-40" },
    { value: "40-50", label: "40-50" },
    { value: "50+", label: "50+" },
    { value: "No preference", label: "No preference" },
  ],
};

export const coachDemographicsPreferenceStep: CoachDiscoveryStep = {
  title: "Coach Demographics Preferences",
  fields: ["coachGender", "coachAges"] as const,
};

export const userPersonalityField = {
  name: "userPersonality",
  subTitle: "Which of these best describes your personality?",
  options: [
    {
      value: "Logical & Strategic Thinker",
      label: "Logical & Strategic Thinker",
      description:
        'Analytical, structured, makes data-driven decisions – Thinkers, Planners, INTJ, Type 5, DISC "C" Profile, Projector',
    },
    {
      value: "Creative & Visionary",
      label: "Creative & Visionary",
      description:
        'Big-picture thinker, enjoys innovation, thrives on ideas – Free-spirited, ENFP, Type 4, DISC "I" Profile, Manifesting Generator',
    },
    {
      value: "Empathetic & Supportive",
      label: "Empathetic & Supportive",
      description:
        'Deeply values relationships, harmony, and emotional connection – INFJ, Type 2, DISC "S" Profile, Reflector',
    },
    {
      value: "Practical & Action-Oriented",
      label: "Practical & Action-Oriented",
      description:
        'Hands-on, efficient, loves getting things done – ESTJ, Type 3, DISC "D" Profile, Manifestor',
    },
    {
      value: "Intuitive & Reflective",
      label: "Intuitive & Reflective",
      description:
        'Inner-focused, values deep meaning, self-awareness, and mindfulness – INFP, Type 9, DISC "S" Profile, Generator',
    },
    {
      value: "Energetic & Fast-Paced",
      label: "Energetic & Fast-Paced",
      description:
        'Highly driven, adaptable, thrives in dynamic environments – ENTP, Type 7, DISC "I" Profile, Manifesting Generator',
    },
    {
      value: "Skip this question",
      label: "Skip this question",
    },
  ],
};

export const coachPersonalityField = {
  name: "coachPersonality",
  subTitle: "What kind of coach personality would you connect best with?",
  options: [
    {
      value: "Logical & Strategic Thinker",
      label: "Logical & Strategic Thinker",
      description:
        "Helps create structured systems & frameworks for success – Thinkers, Strategists, Planners.",
    },
    {
      value: "Energetic & Motivational",
      label: "Energetic & Motivational",
      description:
        "High-energy, dynamic, and keeps you accountable – Visionaries, Leaders, Achievers.",
    },
    {
      value: "Compassionate & Deep Listener",
      label: "Compassionate & Deep Listener",
      description:
        "Creates a safe space, helps you process emotions – Counselors, Healers, Nurturers",
    },
    {
      value: "Spiritual & Holistic",
      label: "Spiritual & Holistic",
      description:
        "Integrates mindfulness, energy work, or alternative approaches – Intuitive, Energy Practitioners, Guides",
    },
    {
      value: "Action-Oriented & Results-Driven",
      label: "Action-Oriented & Results-Driven",
      description:
        "Takes a no-nonsense approach, Focuses on measurable outcomes – Entrepreneurs, Coaches, Business Mentors",
    },
    {
      value: "Skip this question",
      label: "Skip this question",
    },
  ],
};

export const personalityStep: CoachDiscoveryStep = {
  title: "Do you want to be matched based on personality compatibility?",
  subTitle:
    "We use a blend of personality insights from DISC, Myers-Briggs (MBTI), Enneagram, and Human Design to match you with a coach who aligns with your natural style. You can skip this step if you prefer.",
  fields: ["userPersonality", "coachPersonality"] as const,
};

export const sessionDeliveryMethodsFeild = {
  name: "sessionDeliveryMethods",
  subTitle: "How would you like your coaching sessions to be conducted?",
  options: [
    { value: "online", label: "Online" },
    { value: "in-person", label: "In-person" },
    {
      value: "hybrid",
      label: "Hybrid (a mix of online and in-person)",
    },
    { value: "No preference", label: "No preference" },
  ],
};

export const prefersCoachLocationField = {
  name: "prefersCoachLocation",
  subTitle: "Do you have a preference for where your coach is based?",
  options: [
    { value: "Yes", label: "Yes" },
    { value: "No preference", label: "No preference" },
  ],
};

export const coachLocationCountryField = {
  name: "coachLocationCountry",
  subTitle: "Where would you like your coach to be from?",
  placeholder: "Country",
};

export const coachLocationRegionField = {
  name: "coachLocationRegion",
  subTitle: "Type a region or city if you have a preference:",
  placeholder: "City or region",
};

export const formOfConductStep: CoachDiscoveryStep = {
  title: "Coaching Session Format",
  fields: [
    "sessionDeliveryMethods",
    "prefersCoachLocation",
    "coachLocationCountry",
    "coachLocationRegion",
  ] as const,
};

export const faithIntegrationsField = {
  name: "faithIntegration" as const,
  options: [
    { value: "Christianity", label: "Christianity" },
    { value: "Islam", label: "Islam" },
    { value: "Judaism", label: "Judaism" },
    { value: "Hinduism", label: "Hinduism" },
    { value: "Buddhism", label: "Buddhism" },
    {
      value: "Spiritual but not Religious",
      label: "Spiritual but not Religious",
    },
    {
      value: "I do not integrate spirituality into my coaching",
      label:
        "I don't want a coach that integrates spirituality into their coaching",
    },
    { value: "No preference", label: "No preference" },
    { value: "Other", label: "Other" },
  ] as const,
} as const;

export const otherFaithIntegrationField = {
  name: "otherFaithIntegration",
  placeholder: "Share your beliefs and values here...",
  maxLength: 500,
};

export const faithBasedCoachingStep: CoachDiscoveryStep = {
  title:
    "Would you like a coach who integrates faith or spirituality into their coaching?",
  fields: ["faithIntegration", "otherFaithIntegration"] as const,
};

export const preferredLanguagesStep: CoachDiscoveryStep = {
  title:
    "Please select up to three preferred languages for coaching from the dropdown list.",
  fields: ["preferredLanguages"] as const,
};

export const personalContextField = {
  name: "personalContext",
  placeholder: "Describe your personal context here...",
  maxLength: 500,
};

export const personalContextStep: CoachDiscoveryStep = {
  title:
    "What specific challenges are you facing or goals you want to achieve through coaching? Sharing this will help us find the best coach for you.",
  fields: ["personalContext"] as const,
};

// Order is important for the validation function to parse the fields for validation on each step
export const coachDiscoverySteps = [
  specificGoalsStep,
  urgencyAndReadinessStep,
  coachingFormatsAndApproachesStep,
  coachPackagesStep,
  coachDemographicsPreferenceStep,
  personalityStep,
  formOfConductStep,
  faithBasedCoachingStep,
  preferredLanguagesStep,
  personalContextStep,
] as const satisfies readonly CoachDiscoveryStep[];

export const coachDiscoveryDefaultValues: CoachDiscoveryForm = {
  goals: [],
  otherGoal: undefined,
  startTime: "",
  support: "",
  coachingFormats: "any",
  coachingApproaches: [],
  coachingPackages: [],
  coachGender: "",
  coachAges: [],
  userPersonality: [],
  coachPersonality: [],
  faithIntegration: [],
  preferredLanguages: [],
  personalContext: "",
  sessionDeliveryMethods: "",
  prefersCoachLocation: "No preference",
  coachLocationRegion: undefined,
  coachLocationCountry: undefined,
  otherFaithIntegration: undefined,
  statusFilters: [],
};

export const coachDiscoveryStepFieldSets = coachDiscoverySteps.map(
  (step) => step.fields,
) as readonly (readonly CoachDiscoveryField[])[];

const createSchemaForFields = (fields: readonly CoachDiscoveryField[]) => {
  const shape = fields.reduce<Record<string, z.ZodTypeAny>>((acc, field) => {
    acc[field] = coachDiscoveryShape[field as keyof typeof coachDiscoveryShape];
    return acc;
  }, {});
  return applySharedRefinements(z.object(shape));
};

const coachDiscoveryProgressiveFieldSets = coachDiscoveryStepFieldSets.map(
  (_fields, index) =>
    coachDiscoveryStepFieldSets
      .slice(0, index + 1)
      .flat() as readonly CoachDiscoveryField[],
);

export const coachDiscoveryProgressiveSchemas =
  coachDiscoveryProgressiveFieldSets.map((fields) =>
    createSchemaForFields(fields),
  );

export const coachDiscoveryAllFields =
  coachDiscoveryStepFieldSets.flat() as readonly CoachDiscoveryField[];

export const coachDiscoveryPartialSchema = CoachDiscoveryBaseSchema.partial();

export const mergeCoachDiscoveryDefaults = (
  values: Partial<CoachDiscoveryForm>,
): CoachDiscoveryForm => ({
  ...coachDiscoveryDefaultValues,
  ...values,
});

export const coachDiscoveryPartialWithDefaultsSchema =
  coachDiscoveryPartialSchema.transform(mergeCoachDiscoveryDefaults);

export const coachDiscoveryStepNumbers = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
] as const;

export type CoachDiscoveryStepNumber =
  (typeof coachDiscoveryStepNumbers)[number];
